import {
  Box,
  Button,
  Flex,
  Input,
  Spinner,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaFilter, FaSearch, FaSlidersH } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import Layout from "../../../components/Layout";
import FilterModal from "../../../components/FilterModal";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  ManagementFunnelCard,
  ManagementFunnelStageGroup,
  ManagementPurchaseOrderCard,
} from "../../../models/Management";
import { getFunnel, updateProcessStatus } from "../../../services/managementService";
import { crm } from "../../../theme";
import ActiveFiltersBar from "../components/ActiveFiltersBar";
import FunnelStageManager from "../components/FunnelStageManager";
import ProcessBoard from "../components/ProcessBoard";
import { managementFilterTypes, useManagementFilters } from "../components/useManagementFilters";

const Funil = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const toast = useToast();
  const { currentCompany } = useCompany();
  const {
    tags,
    setTags,
    filterParams,
    activeFilterCount,
    activeFilterChips,
    clearAllFilters,
    visibleExtraFilters,
    extraFilters,
    filtersOpen,
    onOpenFilters,
    onCloseFilters,
    deps: filterDeps,
  } = useManagementFilters();

  const [stages, setStages] = useState<ManagementFunnelStageGroup[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<ManagementPurchaseOrderCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [search, setSearch] = useState("");
  const [stageManagerOpen, setStageManagerOpen] = useState(false);
  const loadSeq = useRef(0);

  const load = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const result = await getFunnel({ ...filterParams, includeClosed });
      if (seq !== loadSeq.current) return; // resposta de uma chamada mais antiga — ignora
      setStages(result.stages);
      setPurchaseOrders(result.purchaseOrders ?? []);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      console.log("error", error);
      toast({ title: "Não foi possível carregar o funil.", status: "error" });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentCompany, includeClosed, ...filterDeps]);

  const filteredPurchaseOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return purchaseOrders;
    return purchaseOrders.filter(
      (po) =>
        `po ${po.poNumber}`.includes(term) ||
        po.client?.toLowerCase().includes(term) ||
        po.supplier?.toLowerCase().includes(term)
    );
  }, [purchaseOrders, search]);

  const filteredStages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return stages;
    return stages.map((stage) => ({
      ...stage,
      cards: stage.cards.filter(
        (c) =>
          c.ref?.toLowerCase().includes(term) ||
          c.clientName?.toLowerCase().includes(term) ||
          c.responsibleName?.toLowerCase().includes(term)
      ),
    }));
  }, [stages, search]);

  const handleMove = async (card: ManagementFunnelCard, newStatus: string) => {
    try {
      await updateProcessStatus(card.id, newStatus);
      toast({ title: `Processo #${card.ref} movido para ${newStatus}.`, status: "success", duration: 2500 });
    } catch (error) {
      console.log("error", error);
      toast({ title: `Não foi possível mover o processo #${card.ref}.`, status: "error" });
    } finally {
      load();
    }
  };

  const openProcess = (card: ManagementFunnelCard) => {
    window.open(`/process?id=${card.id}`, "_self");
  };

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="hidden">
      <Layout>
        <Box p="24px" bg={crm.pageBg} h="100%">
          <Flex justify="space-between" align="flex-start" mb="20px" flexWrap="wrap" gap="12px">
            <Flex align="center" gap="12px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Box>
                <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                  Funil de Gestão
                </Text>
                <Text color={crm.textMuted} fontSize="sm" mt="2px">
                  Arraste os processos entre as etapas.
                </Text>
              </Box>
            </Flex>
            <Flex gap="10px" align="center" flexWrap="wrap">
              <Flex
                align="center"
                gap="8px"
                bg={crm.surface}
                border={`1px solid ${crm.border}`}
                borderRadius="10px"
                px="12px"
                h="42px"
              >
                <FaSearch color={crm.textFaint} size={12} />
                <Input
                  placeholder="Buscar ref, cliente, responsável..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  border="none"
                  outline="none"
                  _focus={{ boxShadow: "none" }}
                  w="220px"
                  p={0}
                  fontSize="sm"
                />
              </Flex>
              <Flex align="center" gap="8px" px="4px">
                <Switch
                  isChecked={includeClosed}
                  onChange={(e) => setIncludeClosed(e.target.checked)}
                  colorScheme="blue"
                />
                <Text fontSize="sm" color={crm.textMuted}>
                  Mostrar encerrados
                </Text>
              </Flex>
              <Button
                leftIcon={<FaFilter />}
                variant="outline"
                borderColor={activeFilterCount > 0 ? crm.brand : crm.border}
                color={activeFilterCount > 0 ? crm.brand : crm.text}
                _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                borderRadius="10px"
                fontWeight="500"
                onClick={onOpenFilters}
              >
                Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Button>
              <Button
                leftIcon={<FaSlidersH />}
                variant="outline"
                borderColor={crm.border}
                color={crm.text}
                _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                borderRadius="10px"
                fontWeight="500"
                onClick={() => setStageManagerOpen(true)}
              >
                Gerenciar etapas
              </Button>
            </Flex>
          </Flex>

          <ActiveFiltersBar chips={activeFilterChips} onClear={clearAllFilters} />

          {loading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : (
            <ProcessBoard
              stages={filteredStages}
              purchaseOrders={filteredPurchaseOrders}
              onMove={handleMove}
              onCardClick={openProcess}
            />
          )}
        </Box>

        <FilterModal
          isOpen={filtersOpen}
          onClose={onCloseFilters}
          tags={tags}
          setTags={setTags}
          extraFilters={[...visibleExtraFilters, ...extraFilters]}
          types={managementFilterTypes}
        />

        <FunnelStageManager
          isOpen={stageManagerOpen}
          onClose={() => setStageManagerOpen(false)}
          onChanged={load}
        />
      </Layout>
    </Box>
  );
};

export default Funil;
